import { initializeApp, setLogLevel } from "firebase/app";
import {
  getFirestore,
  collection,
  addDoc,
  initializeFirestore,
  FirestoreSettings,
} from "firebase/firestore";
import { getAnalytics } from "firebase/analytics";
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  signOut,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import {
  getStorage,
  ref,
  uploadString,
  getDownloadURL,
  getMetadata,
} from "firebase/storage";

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: import.meta.env.VITE_API_KEY,
  authDomain: import.meta.env.VITE_AUTHDOMAIN,
  projectId: import.meta.env.VITE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_APP_ID,
  measurementId: import.meta.env.VITE_MEASUREMENT_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

const firestoreSettings: FirestoreSettings = {
  ignoreUndefinedProperties: true,
};
// In modular Firebase v9+, settings should be passed when initializing Firestore, not via db.settings()

initializeFirestore(app, firestoreSettings);

// Enable debug logging
setLogLevel("debug");

// Initialize Firestore
const db = getFirestore(app);
// Firestore settings are already set via initializeFirestore above.

// Initialize Analytics (guard for browser only)
// let analytics: ReturnType<typeof getAnalytics> | undefined = undefined;
// if (typeof window !== "undefined" && typeof window.document !== "undefined") {
//   try {
//     analytics = getAnalytics(app);
//   } catch (e) {
//     console.warn("Firebase Analytics not initialized:", e);
//   }
// }

// Initialize Auth
const auth = getAuth(app);

// Initialize Storage
const storage = getStorage(app);

// Set persistence to local
setPersistence(auth, browserLocalPersistence) // Forces Firebase to remember sessions
  .then(() => {
    console.log("✅ Firebase session persistence enabled.");
  })
  .catch((error) => {
    console.error("❌ Error setting Firebase persistence:", error);
  });

// Providers
const googleProvider = new GoogleAuthProvider();
const microsoftProvider = new OAuthProvider("microsoft.com");
const appleProvider = new OAuthProvider("apple.com");

// Cache to track images that don't exist to avoid repeated 404 requests
// Load from localStorage to persist across page reloads
const loadMissingImagesCache = (): Set<string> => {
  try {
    const cached = localStorage.getItem("focusmap-missing-images");
    return cached ? new Set(JSON.parse(cached)) : new Set();
  } catch {
    return new Set();
  }
};

const saveMissingImagesCache = (cache: Set<string>) => {
  try {
    localStorage.setItem("focusmap-missing-images", JSON.stringify([...cache]));
  } catch {
    // Ignore localStorage errors
  }
};

const missingImagesCache = loadMissingImagesCache();

const uploadImageToStorage = async (
  imageDataUrl: string,
  imageName: string,
  metadata?: { customMetadata?: { [key: string]: string } },
) => {
  try {
    const storageRef = ref(storage, `images/${imageName}`);
    await uploadString(storageRef, imageDataUrl, "data_url", metadata);
    // Remove from missing cache since it now exists
    missingImagesCache.delete(imageName);
    saveMissingImagesCache(missingImagesCache);
    // console.log('Image uploaded successfully with metadata:', metadata);
  } catch (error) {
    console.error("Error uploading image:", error);
  }
};

const getImageFromStorage = async (imageName: string) => {
  // If we've already confirmed this image doesn't exist, return null immediately
  if (missingImagesCache.has(imageName)) {
    return null;
  }

  try {
    const storageRef = ref(storage, `images/${imageName}`);

    // Check if file exists by getting metadata first
    try {
      const metadata = await getMetadata(storageRef);
      const url = await getDownloadURL(storageRef);
      const lastUpdated =
        metadata.customMetadata?.lastUpdated || Date.now().toString();
      // Use the stored timestamp for cache busting
      const finalUrl = `${url}&t=${lastUpdated}`;
      return finalUrl;
    } catch (metadataError) {
      // File doesn't exist - add to cache to prevent future requests
      missingImagesCache.add(imageName);
      saveMissingImagesCache(missingImagesCache);
      return null;
    }
  } catch (error) {
    // Fallback error handling
    missingImagesCache.add(imageName);
    saveMissingImagesCache(missingImagesCache);
    return null;
  }
};

// NEW: Uploads a File and returns its public URL
const uploadImageToStorageForPublicUrl = async (
  file: File,
): Promise<string | null> => {
  try {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    const storageRef = ref(storage, `images/${file.name}`);
    await uploadString(storageRef, dataUrl, "data_url");
    const url = await getDownloadURL(storageRef);
    return url;
  } catch (error) {
    console.error("Error uploading image for public URL:", error);
    return null;
  }
};

const logErrorToFirestore = async (errorMessage: string) => {
  try {
    const user = auth.currentUser;
    const userEmail = user ? user.email : "Unknown user";
    await addDoc(collection(db, "logs"), {
      error: errorMessage,
      user: userEmail,
      timestamp: new Date(),
    });
    console.log("Error logged to Firestore");
  } catch (error) {
    //console.error('Error logging into Firestore:', error);
  }
};

export {
  app,
  db,
  auth,
  //analytics,
  googleProvider,
  microsoftProvider,
  appleProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  signOut,
  uploadImageToStorage,
  getImageFromStorage, // Export the new function
  logErrorToFirestore,
  uploadImageToStorageForPublicUrl, // Export the new function
};
